import { usePolling } from '../hooks/usePolling'
import { workersApi, queueApi, etaApi } from '../lib/api'
import WorkerGrid from '../components/WorkerGrid'
import QueueChart from '../components/QueueChart'
import ETADisplay from '../components/ETADisplay'

export default function Dashboard() {
  const { data: workersData, loading: workersLoading } = usePolling({
    fetcher: () => workersApi.list(),
    interval: 5000,
  })

  const { data: queueStats, loading: queueLoading } = usePolling({
    fetcher: () => queueApi.getStats(),
    interval: 5000,
  })

  const { data: etaBasic, loading: etaLoading } = usePolling({
    fetcher: () => etaApi.get('BASIC'),
    interval: 30000,
  })

  const workers = workersData?.workers || []

  // Count by status
  const statusCounts = workers.reduce(
    (acc, w) => {
      acc[w.status] = (acc[w.status] || 0) + 1
      return acc
    },
    {} as Record<string, number>
  )

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500">
          Auto-refreshing every 5 seconds
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Workers</p>
          <p className="text-3xl font-bold text-gray-900">{workers.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-3xl font-bold text-green-600">
            {statusCounts['idle'] || 0}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Processing</p>
          <p className="text-3xl font-bold text-blue-600">
            {statusCounts['processing'] || 0}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Offline</p>
          <p className="text-3xl font-bold text-gray-400">
            {statusCounts['offline'] || 0}
          </p>
        </div>
      </div>

      {/* Queue and ETA */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {queueLoading ? (
          <div className="bg-white rounded-lg shadow p-6 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        ) : queueStats ? (
          <QueueChart stats={queueStats} />
        ) : null}

        {etaLoading ? (
          <div className="bg-white rounded-lg shadow p-6 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        ) : etaBasic ? (
          <ETADisplay eta={etaBasic} serviceLevel="BASIC" />
        ) : null}
      </div>

      {/* Workers */}
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">Workers</h2>
        {workersLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : (
          <WorkerGrid workers={workers} />
        )}
      </div>
    </div>
  )
}
